import { Dialog } from "@kobalte/core/dialog";
import { useNavigate } from "@solidjs/router";
import { PaperclipIcon } from "@storytell/bsky-icons";
import { useStorytellClient } from "@storytell/client";
import { dispatchGlobalEvent } from "@storytell/events";
import type { Asset, Concept, ConceptId, ViewId } from "@storytell/types";
import { StButton } from "@storytell/ui/buttons";
import { TbApps, TbFileText, TbFilter, TbFilterOff, TbHelpCircle, TbMessage2, TbX } from "solid-icons/tb";
import { type Accessor, For, Match, type Setter, Show, Switch } from "solid-js";
import { Dynamic, isServer } from "solid-js/web";
import { useFileUpload } from "~/lib/file-upload";
import { useWire } from "~/wire/WireProvider";
import { NavigationConceptsPane } from "../concepts/NavigationConceptsPane";
import { Filters } from "../filtering/Filters";
import type { FilterBuilder } from "../filtering/filterBuilder";
import { NavigationAssetsPane } from "../layouts/minimal/NavigationAssetsPane";
import { KnowledgePaneViews } from "./KnowledgePaneViews";
import type { EntitySelection } from "./useEntitySelection";

export const KnowledgePaneContent = (props: {
  filterBuilder: FilterBuilder<Asset | Concept>;
  assetsSelection: EntitySelection;
  conceptsSelection: EntitySelection;
  search: Accessor<string>;
  setSearch: Setter<string>;
  filterExpanded: Accessor<boolean>;
  setFilterExpanded: Setter<boolean>;
  hasViewChanges: boolean;
  selectedConcept: ConceptId | "";
  setSelectedConcept: (conceptId: ConceptId | "") => void;
  editView: boolean;
  setEditView: Setter<boolean>;
  activeView: ViewId | "all" | "new";
  preserveFiltersForNewView: Accessor<boolean>;
  setPreserveFiltersForNewView: Setter<boolean>;
  assetToHighlight: string;
  setAssetToHighlight: (assetId: string) => void;
  labelToFilterBy: string;
  setLabelToFilterBy: (labelId: string) => void;
}) => {
  const wire = useWire();
  const { knowledgeUi } = wire;
  const navigate = useNavigate();

  const activeProject = () => {
    const userContext = wire.userContext.get();
    if (!userContext) return undefined;
    return wire.projects.get(userContext.projectId);
  };

  const handleDropZoneClick = () => {
    dispatchGlobalEvent("open-file-picker", undefined);
    wire.knowledgeUi.close();
  };

  return (
    <>
      <Show when={props.selectedConcept === ""}>
        <div class="flex items-center justify-between px-2 border-b border-primary-border pb-2 max-w-full">
          <div class="flex items-center gap-2 flex-auto">
            <KnowledgePaneViews
              hasViewChanges={props.hasViewChanges}
              filterBuilder={props.filterBuilder}
              assetsSelection={props.assetsSelection}
              conceptsSelection={props.conceptsSelection}
              editView={props.editView}
              setEditView={props.setEditView}
            />
          </div>
          <div class="flex items-center gap-2 shrink-0">
            <Dialog.CloseButton as={StButton} class="shrink-0">
              <TbX />
            </Dialog.CloseButton>
          </div>
        </div>

        {/* Chat with All Knowledge row */}
        <Show when={knowledgeUi.activeView() === "all"}>
          <div class="px-2 max-w-full py-1.5 mt-0.5">
            <button
              type="button"
              onClick={() => {
                dispatchGlobalEvent("add-items-to-prompt-scope", {
                  ids: [],
                  allKnowledge: true,
                });
              }}
              class="text-sm text-left cursor-pointer font-medium text-primary-text py-1 px-2 border rounded-lg border-primary-border transition-all hover:bg-panel-hover"
            >
              Chat with{" "}
              <span class="text-transparent bg-gradient-to-r from-violet-600 to-blue-600 dark:from-violet-400 dark:to-blue-400 bg-clip-text">
                @All Knowledge
              </span>
              {activeProject()?.name && (
                <>
                  {" "}
                  in <span class="font-bold">{activeProject()?.name}</span>
                </>
              )}
            </button>
          </div>
        </Show>

        <Show when={knowledgeUi.activeView() !== "all"}>
          <div class="px-2 max-w-full py-1.5">
            <Filters
              activeView={knowledgeUi.activeView()!}
              builder={props.filterBuilder}
              hasViewChanges={props.hasViewChanges}
              editView={props.editView}
              setEditView={props.setEditView}
            />
          </div>
        </Show>

        <div class="px-4 mb-2">
          <div class="w-full relative p-3 mt-2.5">
            <div class="text-sm text-brand mb-2 px-1 font-semibold">
              View{" "}
              {knowledgeUi.activeView() === "all"
                ? "All Knowledge"
                : knowledgeUi.activeView() === "new"
                  ? "New Collection"
                  : wire.views.get(knowledgeUi.activeView() as ViewId)?.name || "Unknown Collection"}{" "}
              as:
            </div>
            {/* biome-ignore lint/a11y/useKeyWithClickEvents: <explanation> */}
            <div
              class="flex items-center w-full relative bg-gray-200 dark:bg-gray-700 rounded-lg p-1 cursor-pointer"
              onClick={() => knowledgeUi.setActiveTab(knowledgeUi.activeTab() === "concepts" ? "assets" : "concepts")}
            >
              <div
                class="absolute left-1 top-1 bottom-1 w-[calc(50%-0.25rem)] bg-white dark:bg-gray-900 rounded-lg transition-all duration-300 shadow-sm border-2 border-brand"
                style={{
                  transform: `translateX(${knowledgeUi.activeTab() === "concepts" ? 0 : 100}%)`,
                }}
              />
              <For
                each={
                  [
                    { id: "concepts", icon: TbApps, label: "Concept Graph" },
                    { id: "assets", icon: TbFileText, label: "Asset Table" },
                  ] as const
                }
              >
                {(tab) => (
                  <div
                    class={`flex-1 flex flex-col items-center justify-center gap-1 py-3 px-4 rounded-lg transition-all duration-300 relative z-10 font-bold text-sm ${
                      tab.id === knowledgeUi.activeTab() ? "text-brand" : "text-gray-500 dark:text-gray-400"
                    }`}
                  >
                    <div class="flex items-center gap-2">
                      <Dynamic component={tab.icon} class="size-4" />
                      <span>{tab.label}</span>
                    </div>
                    <Show when={tab.id === "concepts"}>
                      <span class="text-xs font-normal text-gray-600 dark:text-gray-400 text-center">
                        A graph of the ideas and themes in{" "}
                        {knowledgeUi.activeView() === "all"
                          ? "All Knowledge"
                          : knowledgeUi.activeView() === "new"
                            ? "New Collection"
                            : wire.views.get(knowledgeUi.activeView() as ViewId)?.name || "Unknown Collection"}
                      </span>
                    </Show>
                    <Show when={tab.id === "assets"}>
                      <span class="text-xs font-normal text-gray-600 dark:text-gray-400 text-center">
                        A list of the files in{" "}
                        {knowledgeUi.activeView() === "all"
                          ? "All Knowledge"
                          : knowledgeUi.activeView() === "new"
                            ? "New Collection"
                            : wire.views.get(knowledgeUi.activeView() as ViewId)?.name || "Unknown Collection"}
                      </span>
                    </Show>
                  </div>
                )}
              </For>
            </div>
          </div>
        </div>

        {/* File Upload Drop Zone - removed for custom views */}
        <div class="px-4 mb-4">{/* Custom views message moved to NavigationAssetsPane */}</div>
      </Show>

      <div class="">
        <Switch>
          <Match when={knowledgeUi.activeTab() === "concepts"}>
            <NavigationConceptsPane
              selectedConcept={props.selectedConcept}
              setSelectedConcept={props.setSelectedConcept}
              selection={props.conceptsSelection}
              search={props.search()}
              filterBuilder={props.filterBuilder}
            />
          </Match>
          <Match when={knowledgeUi.activeTab() === "assets"}>
            <NavigationAssetsPane
              selection={props.assetsSelection}
              search={props.search()}
              setSearch={props.setSearch}
              buildFilterFunction={props.filterBuilder.buildFilterFunction()}
              setLabelFilter={props.setLabelToFilterBy}
              assetToHighlight={props.assetToHighlight}
              setAssetToHighlight={props.setAssetToHighlight}
              labelToFilterBy={props.labelToFilterBy}
              setLabelToFilterBy={props.setLabelToFilterBy}
              filterBuilder={props.filterBuilder}
              hasViewChanges={props.hasViewChanges}
              activeView={props.activeView}
              editView={props.editView}
              setEditView={props.setEditView}
              preserveFiltersForNewView={props.preserveFiltersForNewView}
              setPreserveFiltersForNewView={props.setPreserveFiltersForNewView}
            />
          </Match>
        </Switch>
      </div>
    </>
  );
};
